import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { IconApartmentBuildingOutline18 } from 'nucleo-ui-outline-18'
import { OverlayProvider } from 'overlay-kit'
import { useEffect } from 'react'
import { BrandLoader } from '~/components/brand/brand-loader'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { openCreateWorkspaceModal } from '~/components/workspace/create-workspace-modal'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/_authenticated/home')({
  component: RouteComponent,
  head: () => ({ meta: [{ title: titleFor(msg`Home`) }] }),
  // Carried over from the pricing page's Pro CTA through sign-up: this
  // visitor came to upgrade, so land them on Billing instead of Home.
  validateSearch: (search: Record<string, unknown>): { plan?: 'pro' } =>
    search.plan === 'pro' ? { plan: 'pro' } : {},
})

/**
 * The legacy `/home` landing: resolves the caller's last-visited workspace
 * and forwards to its slug URL. Also the sign-in/sign-up `redirect` fallback,
 * so it must handle the "no workspace at all" state with a create CTA.
 */
function RouteComponent() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { plan } = Route.useSearch()
  const { data: workspace } = useQuery(convexQuery(api.workspaces.defaultWorkspace, {}))
  const { data: currentUser } = useQuery(convexQuery(api.users.currentUser, {}))

  // A brand-new account goes to the setup questions instead, and it is worth
  // deciding that HERE rather than one screen later: this is the landing every
  // entry path funnels through, so catching it now is what keeps a new user
  // from seeing the create-a-workspace empty state and then a whole workspace
  // shell before anything has been asked of them.
  //
  // Both flags are server-side and short-lived. `pendingStarterData` is set by
  // the auth callback and cleared when the studio is seeded; `guidedPendingAt`
  // is stamped by that seeding and cleared when setup opens. Between them they
  // cover the new account whose studio has not landed yet and the one whose
  // has, and neither is ever set on an account that has been here before.
  //
  // The second half is qualified on a studio actually existing, and that is
  // load-bearing rather than belt-and-braces: an account carrying a stale
  // `guidedPendingAt` over a deleted workspace would otherwise be sent to a
  // setup whose second step waits forever for a studio. Qualified, it falls
  // through to the create CTA below instead.
  //
  // An account that has already answered is never sent back, whatever the
  // flags say. The setup returns anyone it recognises to `/home`, so without
  // this the two would bounce a stale `guidedPendingAt` between them forever
  // — which is exactly what a flag re-stamped after the flow consumed it
  // used to produce.
  const slug = workspace?.slug ?? null
  const answered = (currentUser?.onboarding?.goals?.length ?? 0) > 0
  const needsSetup =
    !answered &&
    (currentUser?.pendingStarterData === true ||
      (currentUser?.onboarding?.guidedPendingAt !== undefined && slug !== null))

  useEffect(() => {
    if (needsSetup) {
      // The setup runs on the sign-up route, where a password sign-up
      // continues into it without navigating. This is the door for everyone
      // who could not stay in place: a Google sign-up, which leaves the page
      // for the OAuth round trip, and anyone resuming from a bookmark.
      void navigate({
        replace: true,
        search: plan === 'pro' ? { plan, step: 'setup' } : { step: 'setup' },
        to: '/sign-up',
      })
      return
    }
    if (slug !== null) {
      void navigate({
        params: { workspaceSlug: slug },
        replace: true,
        to: plan === 'pro' ? '/$workspaceSlug/settings/billing' : '/$workspaceSlug/home',
      })
    }
  }, [navigate, needsSetup, plan, slug])

  if (workspace === null && !needsSetup && currentUser !== undefined) {
    // The create-workspace modal mounts through overlay-kit, whose provider
    // normally lives inside the workspace shell — absent here, so bring one.
    return (
      <OverlayProvider>
        <div className="flex h-full items-center justify-center bg-canvas">
          <EmptyState
            description={t`Create a workspace to start using Planno.`}
            icon={IconApartmentBuildingOutline18}
            title={t`You don't have a workspace yet`}
          >
            <Button onClick={openCreateWorkspaceModal}>
              <Trans>Create workspace</Trans>
            </Button>
          </EmptyState>
        </div>
      </OverlayProvider>
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-canvas">
      <BrandLoader />
    </div>
  )
}
